import { z } from 'zod';

/**
 * @aiPurpose CRM Lead comment payload constants (#1084). The 4000-char
 *   cap mirrors the persistence column and keeps revision-history rows
 *   deterministically small; the activity feed never embeds full
 *   bodies, only ids.
 */
export const LEAD_COMMENT_BODY_MIN = 1;
export const LEAD_COMMENT_BODY_MAX = 4000;
export const LEAD_COMMENT_PREVIEW_MAX = 200;

const LeadCommentBodySchema = z
  .string()
  .min(LEAD_COMMENT_BODY_MIN)
  .max(LEAD_COMMENT_BODY_MAX)
  // Reject all-whitespace bodies at the wire layer. A trim() refine
  // matching the empty string fails before service-layer normalization.
  .refine(
    (s) => s.trim().length >= LEAD_COMMENT_BODY_MIN,
    'comment body must not be whitespace-only',
  );

/**
 * @aiPurpose author reference attached to a comment row at read time.
 *   Resolved from `LeadComment.author` so the client renders a name
 *   without an extra users lookup.
 * @aiPii `name` is the display name from the User row. Visibility is
 *   already enforced by the parent Lead-scope gate.
 */
export const LeadCommentAuthorRefSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string().min(1),
  })
  .strict();
export type LeadCommentAuthorRef = z.infer<typeof LeadCommentAuthorRefSchema>;

/**
 * @aiPurpose wire projection of a single revision row.
 * @aiInvariant `body` snapshots the PRIOR comment body before the
 *   edit that produced this row. The chronological order is
 *   `editedAt` ASC = oldest revision first; the live body lives on
 *   the parent `LeadCommentSchema`.
 * @aiPii same PII class as the live body — surfaced only to actors
 *   that already pass the parent Lead-visibility gate.
 */
export const LeadCommentRevisionSchema = z
  .object({
    id: z.string().uuid(),
    body: z.string().min(LEAD_COMMENT_BODY_MIN).max(LEAD_COMMENT_BODY_MAX),
    editedBy: LeadCommentAuthorRefSchema.nullable(),
    editedAt: z.string().datetime(),
  })
  .strict();
export type LeadCommentRevision = z.infer<typeof LeadCommentRevisionSchema>;

/**
 * @aiPurpose wire projection of a comment row. `revisions` is the
 *   ordered append-only edit history; `revisionCount` is precomputed
 *   so list views can render the "edited" badge without scanning the
 *   array.
 */
export const LeadCommentSchema = z
  .object({
    id: z.string().uuid(),
    leadId: z.string().uuid(),
    author: LeadCommentAuthorRefSchema.nullable(),
    body: z.string().min(LEAD_COMMENT_BODY_MIN).max(LEAD_COMMENT_BODY_MAX),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    revisionCount: z.number().int().nonnegative(),
    revisions: z.array(LeadCommentRevisionSchema),
  })
  .strict();
export type LeadComment = z.infer<typeof LeadCommentSchema>;

/**
 * @aiPurpose request envelope for POST `/leads/:id/comments`.
 * @aiInvariant `.strict()` rejects unknown keys so a typo on the
 *   client surfaces as 400 VALIDATION_ERROR instead of being silently
 *   dropped.
 */
export const CreateLeadCommentRequestSchema = z.object({ body: LeadCommentBodySchema }).strict();
export type CreateLeadCommentRequest = z.infer<typeof CreateLeadCommentRequestSchema>;

/**
 * @aiPurpose request envelope for PATCH `/leads/:id/comments/:commentId`.
 *   v1 restricts edits to the comment AUTHOR; the service rejects
 *   non-author callers with 403.
 */
export const UpdateLeadCommentRequestSchema = z.object({ body: LeadCommentBodySchema }).strict();
export type UpdateLeadCommentRequest = z.infer<typeof UpdateLeadCommentRequestSchema>;

export const LeadCommentsListResponseSchema = z
  .object({ items: z.array(LeadCommentSchema) })
  .strict();
export type LeadCommentsListResponse = z.infer<typeof LeadCommentsListResponseSchema>;

export const LeadCommentResponseSchema = z.object({ comment: LeadCommentSchema }).strict();
export type LeadCommentResponse = z.infer<typeof LeadCommentResponseSchema>;
